import cn from './cn.js';

/** Surface card. Use `padding={false}` when the body manages its own padding (tables, lists). */
export function Card({ as: Comp = 'div', className, padding = true, interactive = false, children, ...props }) {
  return (
    <Comp
      className={cn(
        'rounded-2xl bg-white ring-1 ring-gray-200 shadow-card',
        padding && 'p-5 sm:p-6',
        interactive && 'transition-shadow duration-150 hover:shadow-card-hover',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

export function CardHeader({ title, description, actions, icon: Icon, className, children, divider = false }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between',
        divider && 'border-b border-gray-100 pb-4',
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          {title && <h3 className="text-base font-semibold leading-6 text-gray-900">{title}</h3>}
          {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
          {children}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }) {
  return <div className={cn('px-5 py-4 sm:px-6', className)}>{children}</div>;
}

export function CardFooter({ className, children }) {
  return (
    <div className={cn('flex flex-wrap items-center justify-end gap-2 rounded-b-2xl border-t border-gray-100 bg-gray-50 px-5 py-3 sm:px-6', className)}>
      {children}
    </div>
  );
}

/** Section: a titled block of content with optional actions, used to group cards. */
export function Section({ title, description, actions, className, children, id }) {
  return (
    <section id={id} className={cn('space-y-4', className)} aria-label={typeof title === 'string' ? title : undefined}>
      {(title || actions) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            {title && <h2 className="text-lg font-semibold leading-7 tracking-tight text-gray-900">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export default Card;
